/**
 * Lightweight SSE client singleton.
 * Connects to the BFF events stream and allows components to subscribe to event types.
 * Auto-reconnects on connection loss.
 */

import { apiUrl } from './runtime-config';

type Handler = (payload: unknown) => void;

const _handlers: Map<string, Set<Handler>> = new Map();
let _es: EventSource | null = null;
let _reconnectTimer: ReturnType<typeof setTimeout> | null = null;

function _handleMessage(raw: string): void {
  try {
    const envelope = JSON.parse(raw) as { type: string; payload: unknown };
    const handlers = _handlers.get(envelope.type);
    if (handlers) for (const h of handlers) h(envelope.payload);
  } catch {
    // ignore malformed messages
  }
}

/** Reserved event type fired every time the SSE stream (re)connects, including the
 *  very first connection. Subscribers can use this to resync state that may have
 *  drifted while disconnected (e.g. re-fetch a count instead of relying solely on
 *  push events, which can be missed during a reconnect gap). */
export const SSE_CONNECTED = '__sse_connected__';

function _connect(): void {
  if (_es) return;
  _es = new EventSource(apiUrl('/events/stream'));

  _es.onmessage = (e) => _handleMessage(e.data as string);

  _es.onopen = () => {
    const handlers = _handlers.get(SSE_CONNECTED);
    if (handlers) for (const h of handlers) h(undefined);
  };

  _es.onerror = () => {
    _es?.close();
    _es = null;
    if (_reconnectTimer) clearTimeout(_reconnectTimer);
    _reconnectTimer = setTimeout(_connect, 5000);
  };
}

/** Start the SSE connection. Call once at app startup. */
export function startSse(): void {
  _connect();
}

/** Stop the SSE connection (e.g. on logout). */
export function stopSse(): void {
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
  _es?.close();
  _es = null;
}

/** Subscribe to a specific event type. Returns an unsubscribe function. */
export function onSseEvent(type: string, handler: Handler): () => void {
  if (!_handlers.has(type)) _handlers.set(type, new Set());
  _handlers.get(type)!.add(handler);
  return () => _handlers.get(type)?.delete(handler);
}
